import React, { useEffect, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { LAYOUTS } from '../../../utilities/layout';

import { PageBackIcon, PageForwardIcon, CaretLeftIcon, CaretRightIcon, CloseIcon, SearchIcon } from '@seismic/icons';

import './styles.scss';

const ToolbarSearchCloseButton = ({ closeSearch, isMobile }) => {
  const classes = classNames({
    'up-close-button': true,
    'up-search-control-button': true,
    'up-mobile': isMobile
  });

  return (
    <div className={classes} onClick={closeSearch} datatest={'toolbarSearchCloseButton'}>
      <CloseIcon />
    </div>
  );
};

const ToolbarSearchNavigationalButton = ({ datatest, disableButtons, mobile, onClick, icon }) => {
  const classes = classNames({
    'up-search-control-button': true,
    'up-button-disabled': disableButtons,
    'up-mobile': mobile
  });

  return (
    <div className={classes} onClick={onClick} datatest={datatest}>
      {icon}
    </div>
  );
};

const ToolbarSearchInputContainer = React.forwardRef(
  ({ currentHit, layout, mobile, onChange, onKeyDown, searchTerm, totalHits }, ref) => {
    const { t } = useTranslation();

    const classes = classNames({
      'up-search-input-container': true,
      'up-mobile': mobile
    });

    return (
      <div className={classes} datatest={'toolbarSearchInputContainer'}>
        <SearchIcon className="up-search-icon" />
        <input
          className={mobile && 'up-mobile'}
          onChange={onChange}
          onKeyDown={onKeyDown}
          id="up-searchInput"
          datatest="searchInput"
          placeholder={t('Toolbar.SearchInput.SearchText')}
          ref={ref}
          value={searchTerm}
        />
        {layout !== LAYOUTS.SMALL && !!searchTerm.length && (
          <div className={'up-hit-counter'} datatest={'hitCounter'}>
            {t('Toolbar.SearchInput.CurrentOfTotalResults', {
              defaultValue: `${currentHit} of ${totalHits}`,
              replace: {
                current: currentHit,
                total: totalHits
              }
            })}
          </div>
        )}
      </div>
    );
  }
);

const ToolbarSearch = (props, ref) => {
  const handleTermChanged = e => props.handleSearch(e.target.value);
  const disableButtons = !props.totalHits;

  const handleOnKeyDown = evt => {
    if (evt.key === 'Enter') {
      props.handleNext();
    }
  };

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus() {
      inputRef.current.focus();
    },

    addEventListener(...args) {
      return inputRef.current.addEventListener(...args);
    },

    removeEventListener(...args) {
      return inputRef.current.removeEventListener(...args);
    }
  }));

  useEffect(() => {
    if (props.autofocus) {
      const container = containerRef.current;
      const input = inputRef.current;
      let eventHandler = () => {};

      if (!isNil(container)) {
        if (props.showSearch) {
          eventHandler = () => {
            if (!isNil(input)) {
              input.focus();
            }
          };
          container.addEventListener('transitionend', eventHandler);
          return () => container.removeEventListener('transitionend', eventHandler);
        }

        eventHandler = () => {
          if (!isNil(input)) {
            input.blur();
          }
        };
        container.addEventListener('transitionstart', eventHandler);
        return () => container.removeEventListener('transitionstart', eventHandler);
      }
    }
  }, [containerRef.current, inputRef.current, props.autofocus, props.showSearch]);

  // The mobile layout differs significantly from the desktop one
  // So we need the isMobile check to lay everything out correctly
  if (props.isMobile) {
    return (
      <div className={`up-search-container up-mobile ${props.showSearch ? 'up-show' : 'up-hide'}`} ref={containerRef}>
        <div className="up-search-controls up-mobile">
          <ToolbarSearchCloseButton closeSearch={props.closeSearch} isMobile={props.isMobile} />
        </div>
        <ToolbarSearchInputContainer
          mobile
          currentHit={props.currentHit}
          layout={props.layout}
          onChange={handleTermChanged}
          ref={inputRef}
          searchTerm={props.searchTerm}
          totalHits={props.totalHits}
        />
        <div className="up-search-controls up-mobile">
          <ToolbarSearchNavigationalButton
            mobile
            datatest={'toolbarSearchPreviousButton'}
            disableButtons={disableButtons}
            onClick={props.handlePrevious}
            icon={<CaretLeftIcon size="lg" />}
          />
          <ToolbarSearchNavigationalButton
            mobile
            datatest={'toolbarSearchNextButton'}
            disableButtons={disableButtons}
            onClick={props.handleNext}
            icon={<CaretRightIcon size="lg" />}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`up-search-container ${props.showSearch ? 'up-show' : 'up-hide'}`} ref={containerRef}>
      <ToolbarSearchInputContainer
        currentHit={props.currentHit}
        layout={props.layout}
        onChange={handleTermChanged}
        onKeyDown={handleOnKeyDown}
        ref={inputRef}
        searchTerm={props.searchTerm}
        totalHits={props.totalHits}
      />
      <div className="up-search-controls">
        <ToolbarSearchNavigationalButton
          datatest={'toolbarSearchPreviousButton'}
          disableButtons={disableButtons}
          onClick={props.handlePrevious}
          icon={<PageBackIcon />}
        />
        <ToolbarSearchNavigationalButton
          datatest={'toolbarSearchNextButton'}
          disableButtons={disableButtons}
          onClick={props.handleNext}
          icon={<PageForwardIcon />}
        />
        <div className="up-control-separator" datatest={'toolbarSearchControlSeparator'} />
        <ToolbarSearchCloseButton closeSearch={props.closeSearch} />
      </div>
    </div>
  );
};

export default React.forwardRef(ToolbarSearch);
